// Guidebook hub: a single popup that lists every product tour available in
// the vendor portal so vendors can discover and replay any walkthrough from
// one place. Replaces the per-tab "start the current tab's tour" behaviour
// of the old Guide button.

import { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, PlayCircle, BookOpen } from "lucide-react";
import { useTour } from "./TourProvider";
import { TOUR_HUB_ORDER, tourIdForTab, type TourConfig } from "./registry";

interface GuidebookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Currently active dashboard tab — the matching tour is highlighted as recommended. */
  activeTab: string;
  /** When the Hosts tab is on its detail sub-view, switch the recommended tour to the per-host detail tour. */
  hostsSubView?: "list" | "hostDetail";
  /** When false, owner-only tours (e.g. Team) are hidden from the hub. */
  isOwner: boolean;
  /**
   * Callback to switch the vendor portal to a specific tab before the tour
   * spotlight activates. Required so tours that target tab-specific DOM nodes
   * can navigate to the correct tab when the vendor starts them from a
   * different tab in the Guidebook.
   */
  onNavigateTab?: (tab: string) => void;
  /**
   * Callback to drill the Hosts tab into its per-host detail sub-view before
   * the `hostsDetail` tour activates. The detail tour's targets only mount
   * once a host card is open, so starting it from any other screen requires
   * opening the first host card first.
   */
  onOpenHostDetail?: () => void;
  /**
   * Whether the vendor has any host clients. When false the `hostsDetail`
   * tour is hidden from the hub — there is no host card to drill into, so the
   * walkthrough would have nothing to spotlight.
   */
  hasHosts?: boolean;
}

export function GuidebookDialog({ open, onOpenChange, activeTab, hostsSubView, isOwner, onNavigateTab, onOpenHostDetail, hasHosts = true }: GuidebookDialogProps) {
  const { progress, startTour } = useTour();
  const subView = activeTab === "hosts" ? (hostsSubView ?? null) : null;
  const recommendedTourId = tourIdForTab(activeTab, subView);

  const tours = useMemo<TourConfig[]>(() => {
    return TOUR_HUB_ORDER.filter((t) => {
      if (t.id === "team") return isOwner;
      if (t.id === "hostsDetail") return hasHosts;
      return true;
    });
  }, [isOwner, hasHosts]);

  function statusBadge(tourId: string) {
    const rec = progress[tourId];
    if (!rec) {
      return <Badge variant="outline" data-testid={`badge-tour-status-${tourId}`}>Not started</Badge>;
    }
    if (rec.status === "completed") {
      return (
        <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100" data-testid={`badge-tour-status-${tourId}`}>
          <CheckCircle2 className="h-3 w-3 mr-1" /> Completed
        </Badge>
      );
    }
    if (rec.status === "skipped") {
      return <Badge variant="outline" data-testid={`badge-tour-status-${tourId}`}>Skipped</Badge>;
    }
    return (
      <Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100" data-testid={`badge-tour-status-${tourId}`}>
        In progress
      </Badge>
    );
  }

  function handleStart(tourId: string) {
    onOpenChange(false);

    const tour = TOUR_HUB_ORDER.find((t) => t.id === tourId);

    // The per-host detail tour lives on the Hosts tab's drill-in sub-view, so
    // it needs both a tab switch (when off the Hosts tab) and a host card to be
    // open. Open the first card unless one is already open. The spotlight
    // retries measurement (8 × 150 ms) and renders a centred tooltip in the
    // meantime, so it gracefully covers the host-still-loading window and the
    // no-hosts case.
    if (tourId === "hostsDetail") {
      const needsTabSwitch = activeTab !== "hosts" && onNavigateTab !== undefined;
      if (needsTabSwitch) onNavigateTab!("hosts");
      const alreadyOnDetail = activeTab === "hosts" && hostsSubView === "hostDetail";
      if (!alreadyOnDetail) onOpenHostDetail?.();
      // Give the tab paint + host card mount time before spotlighting; longer
      // when we also had to switch tabs from scratch.
      const delay = needsTabSwitch ? 450 : alreadyOnDetail ? 150 : 400;
      window.setTimeout(() => startTour(tourId), delay);
      return;
    }

    const needsTabSwitch =
      tour?.requiredTab !== undefined &&
      tour.requiredTab !== activeTab &&
      onNavigateTab !== undefined;

    if (needsTabSwitch) {
      // Navigate to the required tab first, then wait for React to mount the
      // new tab's content before the spotlight searches for DOM targets.
      // 300 ms covers a typical tab content paint; the SpotlightOverlay also
      // retries measurement up to 8 × 150 ms as a safety net.
      onNavigateTab!(tour!.requiredTab!);
      window.setTimeout(() => startTour(tourId), 300);
    } else {
      // Already on the right tab (or tour is tab-agnostic) — defer only long
      // enough for the dialog close animation to clear.
      window.setTimeout(() => startTour(tourId), 150);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="dialog-guidebook">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Guidebook
          </DialogTitle>
          <DialogDescription>
            Pick a guided tour. We auto-play one the first time you visit each tab — you can replay any of them here whenever you want.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2">
          <ul className="grid gap-3 sm:grid-cols-2">
            {tours.map((tour) => {
              const Icon = tour.icon ?? BookOpen;
              const isRecommended = tour.id === recommendedTourId;
              const stepCount = tour.steps.length;
              return (
                <li
                  key={tour.id}
                  className={`relative rounded-lg border p-4 flex flex-col gap-3 transition-colors ${
                    isRecommended ? "border-primary/60 bg-primary/5" : "border-border bg-card"
                  }`}
                  data-testid={`card-guidebook-tour-${tour.id}`}
                >
                  {isRecommended && (
                    <Badge className="absolute -top-2 left-3" data-testid={`badge-tour-recommended-${tour.id}`}>
                      For this page
                    </Badge>
                  )}
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 rounded-md p-2 ${isRecommended ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-sm leading-tight" data-testid={`text-guidebook-tour-name-${tour.id}`}>
                          {tour.name ?? tour.id}
                        </h3>
                        {statusBadge(tour.id)}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1" data-testid={`text-guidebook-tour-summary-${tour.id}`}>
                        {tour.summary ?? ""}
                      </p>
                      <p className="text-[11px] text-muted-foreground/80 mt-1.5">
                        {stepCount} step{stepCount === 1 ? "" : "s"}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant={isRecommended ? "default" : "outline"}
                      onClick={() => handleStart(tour.id)}
                      data-testid={`button-guidebook-start-${tour.id}`}
                      className="gap-1.5"
                    >
                      <PlayCircle className="h-4 w-4" />
                      {progress[tour.id]?.status === "completed" || progress[tour.id]?.status === "skipped"
                        ? "Replay tour"
                        : progress[tour.id]?.status === "in_progress"
                        ? "Resume tour"
                        : "Start tour"}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
